import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { REDIRECT_CONFIG } from '../injection-tokens/redirect-config.token';

/**
 * Represents a mapping record that links old structure/node IDs to new category/document IDs
 */
export interface MappingRecord {
  structureid: string; // Old structure ID from legacy system
  catid: string; // New category ID in current system
  docid: string; // New document ID in current system
  nodeid: string; // Old node ID from legacy system
  cathasdocuments: string; // Indicate if the category has a document
}

/**
 * Represents a static redirect that redirects to a new URL
 */
export interface StaticRedirect {
  source: string; // Source URL from legacy system
  target: string; // Target URL in current system
  includeQueryParams?: string; // Optional CSV flag: TRUE or FALSE
}

const shouldIncludeOriginalQueryParams = (redirect: StaticRedirect): boolean => {
  return redirect.includeQueryParams === 'TRUE';
};

const appendQueryParams = (target: string, sourceQuery: string): string => {
  if (!sourceQuery || sourceQuery === '?') {
    return target;
  }

  const queryToAppend = sourceQuery.startsWith('?') ? sourceQuery.slice(1) : sourceQuery;

  if (!queryToAppend) {
    return target;
  }

  const [targetWithoutHash, hashFragment] = target.split('#');
  const [targetPath, targetQuery] = targetWithoutHash.split('?');
  const combinedQuery = targetQuery ? `${targetQuery}&${queryToAppend}` : queryToAppend;

  return `${targetPath}?${combinedQuery}${hashFragment ? `#${hashFragment}` : ''}`;
};

/**
 * Guard function that checks if current URL should be redirected
 *
 * @returns false if redirect occurred, true if no redirect needed
 */
export async function redirectGuard(): Promise<boolean> {
  const httpClient = inject(HttpClient);
  const redirectConfig = inject(REDIRECT_CONFIG);
  const redirect = await getRedirectUrl(httpClient, redirectConfig);

  if (redirect) {
    window.location.href = redirect;

    return false;
  }

  return true;
}

/**
 * Determines if current URL matches any legacy patterns and returns redirect URL
 *
 * @param httpClient - HttpClient instance for fetching mapping/redirect data
 * @param redirectConfig - Redirect configuration with API URL and target host
 * @returns Redirect URL if pattern matches, null otherwise
 */
export async function getRedirectUrl(
  httpClient: HttpClient,
  redirectConfig: { api: { url: string }; targetHost: string }
): Promise<string | null> {
  return new Promise(async (resolve) => {
    const mappingRecords: MappingRecord[] = await getMappingRecords(httpClient);
    const staticRedirects: StaticRedirect[] = await getStaticRedirects(httpClient);

    // Use original URL with hash before Angular processes it
    const urlToCheck = window.location.href;

    // Build request parameters for static redirects check
    const url = location.pathname + location.search + location.hash;
    const requestPath = location.pathname;
    const requestQuery = location.search;

    // Prefer exact match (path/query/hash), then fallback to path-only.
    const staticRedirect =
      staticRedirects.find((redirect) => redirect.source.toLowerCase().endsWith(url.toLowerCase())) ||
      staticRedirects.find(
        (redirect) =>
          shouldIncludeOriginalQueryParams(redirect) &&
          redirect.source.toLowerCase().endsWith(requestPath.toLowerCase())
      );

    if (staticRedirect) {
      if (shouldIncludeOriginalQueryParams(staticRedirect)) {
        resolve(appendQueryParams(staticRedirect.target, requestQuery));
        return;
      }

      resolve(staticRedirect.target);
      return;
    }

    // Escape dots in hostname for regex
    const hostnameRegex = location.hostname;
    const port = location.port && location.port !== '80' && location.port !== '443' ? `:${location.port}` : '';
    const protocol = location.protocol === 'https:' ? 'https' : 'http';

    // Define URL patterns to match legacy URLs and their redirect targets
    const patterns = [
      {
        type: 'Asset',
        // Matches: /website/..., /doc/..., /app/... paths
        regex: new RegExp(`^https?://${hostnameRegex}${port}/(website|doc|app)/(.+)$`, 'i'),
        target: (match: string[]) => `${redirectConfig.api.url}asset?path=/${match[1]}/${match[2]}`,
      },
      {
        type: 'DWG',
        // Matches: /media/call.php?id=X&path=Y&type=dwg&NID=Z with optional additional parameters
        regex: new RegExp(
          `^https?://${hostnameRegex}${port}/media/call\\.php\\?(?:[^&]*&)*id=\\d+(?:&[^&]*)*&path=([^&]+)(?:&[^&]*)*&type=dwg(?:&[^&]*)*&NID=\\d+(?:&[^&]*)*$`,
          'i'
        ),
        target: (match: string[]) => {
          const fileName = match[1].endsWith('.dwg') ? match[1] : `${match[1]}.dwg`;

          return `${redirectConfig.api.url}asset?path=/dwg/${fileName}`;
        },
      },
      {
        type: 'Search',
        // Matches: /web/main/SinglePageApp.php?searchText=... with optional additional parameters
        regex: new RegExp(
          `^https?://${hostnameRegex}${port}/(?:web|aks|jansen)/main/SinglePageApp\\.php\\?(?:[^&]*&)*searchText=([^&\\s#]+)(?:&[^&]*)*`,
          'i'
        ),
        target: (match: string[]) => `${protocol}://${redirectConfig.targetHost}/en/search?searchTerm=${match[1]}`,
      },
      {
        type: 'DocumentCategoryHash',
        // Matches: /web/main/SinglePageApp.php?fwd=true#structureId with optional additional parameters
        regex: new RegExp(
          `^https?://${hostnameRegex}${port}/(?:web|aks|jansen)/main/SinglePageApp\\.php(?:\\?(?:[^#]*))?#(\\d+)`,
          'i'
        ),
        target: (match: string[]) => {
          // Look up new category and document IDs using old structure ID
          const mapped_catid = mappingRecords.find((record: MappingRecord) => record.structureid === match[1])?.catid;
          const mapped_docid = mappingRecords.find((record: MappingRecord) => record.structureid === match[1])?.docid;

          if (!mapped_catid && !mapped_docid) {
            return null;
          }

          if (!mapped_docid) {
            const cat_has_docs = mappingRecords.find(
              (record: MappingRecord) => record.structureid === match[1]
            )?.cathasdocuments;

            if (!cat_has_docs || cat_has_docs === 'FALSE') {
              return `${protocol}://${redirectConfig.targetHost}/de/categories/${mapped_catid}`;
            }

            return `${protocol}://${redirectConfig.targetHost}/de/categories/${mapped_catid}/documents`;
          }

          return `${protocol}://${redirectConfig.targetHost}/de/categories/${mapped_catid}/documents/${mapped_docid}`;
        },
      },
      {
        type: 'DocumentParamsHash',
        // Matches: /web/main/SinglePageApp.php?PN=page&LID=lang&fwd=true#structureId with optional additional parameters
        regex: new RegExp(
          `^https?://${hostnameRegex}${port}/(?:web|aks|jansen)/main/SinglePageApp\\.php\\?(?:[^#]*&)*PN=(\\d+)(?:&[^#]*)*?(?:&LID=([a-z]+))?(?:&[^#]*)*?(?:&fwd=true)?(?:&[^#]*)*#(\\d+)$`,
          'i'
        ),
        target: (match: string[]) => {
          // Look up new category and document IDs using old structure ID
          const mapped_catid = mappingRecords.find((record: MappingRecord) => record.structureid === match[3])?.catid;
          const mapped_docid = mappingRecords.find((record: MappingRecord) => record.structureid === match[3])?.docid;

          if (!mapped_catid && !mapped_docid) {
            return null;
          }

          if (!mapped_docid) {
            const cat_has_docs = mappingRecords.find(
              (record: MappingRecord) => record.structureid === match[3]
            )?.cathasdocuments;

            if (!cat_has_docs || cat_has_docs === 'FALSE') {
              return `${protocol}://${redirectConfig.targetHost}/${match[2] ?? 'en'}/categories/${mapped_catid}`;
            }

            return `${protocol}://${redirectConfig.targetHost}/${
              match[2] ?? 'en'
            }/categories/${mapped_catid}/documents`;
          }

          return `${protocol}://${redirectConfig.targetHost}/${
            match[2] ?? 'en'
          }/categories/${mapped_catid}/documents/${mapped_docid}/${match[2] ?? 'en'}?page=${match[1]}`;
        },
      },
      {
        type: 'DocumentOuterCatLink',
        // Matches: /web/main/OuterCatLink.php?NID=nodeId&PN=page&LID=lang with optional additional parameters
        regex: new RegExp(
          `^https?://${hostnameRegex}${port}/(?:web|aks|jansen)/main/OuterCatLink\\.php\\?(?:[^&]*&)*NID=(\\d+)(?:&[^&]*)*(?:&PN=(\\d+))?(?:&[^&]*)*?(?:&LID=([a-z]+))?(?:&[^&]*)*`,
          'i'
        ),
        target: (match: string[]) => {
          // Look up new category and document IDs using old node ID
          const mapped_catid = mappingRecords.find((record: MappingRecord) => record.nodeid === match[1])?.catid;
          const mapped_docid = mappingRecords.find((record: MappingRecord) => record.nodeid === match[1])?.docid;

          if (!mapped_catid && !mapped_docid) {
            return null;
          }

          if (!mapped_docid) {
            const cat_has_docs = mappingRecords.find(
              (record: MappingRecord) => record.nodeid === match[1]
            )?.cathasdocuments;

            if (!cat_has_docs || cat_has_docs === 'FALSE') {
              return `${protocol}://${redirectConfig.targetHost}/${match[3] ?? 'en'}/categories/${mapped_catid}`;
            }

            return `${protocol}://${redirectConfig.targetHost}/${
              match[3] ?? 'en'
            }/categories/${mapped_catid}/documents`;
          }

          const pageParam = match[2] ? `?page=${match[2]}` : '';

          return `${protocol}://${redirectConfig.targetHost}/${
            match[3] ?? 'en'
          }/categories/${mapped_catid}/documents/${mapped_docid}${match[3] ? `/${match[3]}` : ''}${pageParam}`;
        },
      },
      {
        type: 'DocumentSearchHash',
        // Matches: /web/main/SinglePageApp.php?fwd=true&search=term#structureId with optional additional parameters
        regex: new RegExp(
          `^https?://${hostnameRegex}${port}/(?:web|aks|jansen)/main/SinglePageApp\\.php\\?(?:[^#]*&)*?(?:fwd=true&)?search=([^#]+)(?:&[^#]*)*#(\\d+)$`,
          'i'
        ),
        target: (match: string[]) => `${protocol}://${redirectConfig.targetHost}/en/search?searchterm=${match[1]}`,
      },
      {
        type: 'DocumentOuterCatLinkLLNK',
        // Matches: /web/main/OuterCatLink.php?LLNK=???;structureId;page with optional additional parameters
        regex: new RegExp(
          `^https?://${hostnameRegex}${port}/(?:web|aks|jansen)/main/OuterCatLink\\.php\\?(?:[^&]*&)*LLNK=[^;]+;(\\d+);(\\d+)(?:&[^&]*)*`,
          'i'
        ),
        target: (match: string[]) => {
          // Look up new category and document IDs using old structure ID
          const mapped_catid = mappingRecords.find((record: MappingRecord) => record.structureid === match[1])?.catid;
          const mapped_docid = mappingRecords.find((record: MappingRecord) => record.structureid === match[1])?.docid;

          if (!mapped_catid && !mapped_docid) {
            return null;
          }

          if (!mapped_docid) {
            const cat_has_docs = mappingRecords.find(
              (record: MappingRecord) => record.structureid === match[1]
            )?.cathasdocuments;

            if (!cat_has_docs || cat_has_docs === 'FALSE') {
              return `${protocol}://${redirectConfig.targetHost}/en/categories/${mapped_catid}`;
            }

            return `${protocol}://${redirectConfig.targetHost}/en/categories/${mapped_catid}/documents`;
          }

          return `${protocol}://${redirectConfig.targetHost}/en/categories/${mapped_catid}/documents/${mapped_docid}?page=${match[2]}`;
        },
      },
    ];

    // Check each pattern against current URL
    for (const pattern of patterns) {
      const match = urlToCheck.match(pattern.regex);

      if (match) {
        const targetUrl = pattern.target(match);
        resolve(targetUrl);
        return;
      }
    }

    resolve(null);
  });
}

/**
 * Fetches mapping records from CSV file to translate old IDs to new ones
 *
 * @returns Array of mapping records (initially empty, populated asynchronously)
 */
export async function getMappingRecords(httpClient: HttpClient): Promise<MappingRecord[]> {
  const queryMappingRecords = async () => {
    const port = location.port && location.port !== '80' && location.port !== '443' ? `:${location.port}` : '';
    const protocol = location.protocol === 'https:' ? 'https' : 'http';

    const response = await firstValueFrom(
      httpClient.get(`${protocol}://${location.hostname || 'localhost'}${port}/mapping_records.csv`, {
        responseType: 'text',
      })
    );

    return response;
  };

  return new Promise((resolve) => {
    let mappingRecords: MappingRecord[] = [];

    // Parse CSV response and populate mapping records
    queryMappingRecords()
      .then((response) => {
        const headerLine = response.split('\n')[0]; // Get header row
        const separator = headerLine.includes(';') ? ';' : ',';
        const header = headerLine.split(separator);
        const lines = response.split('\n').slice(1); // Skip header row

        mappingRecords = lines.map((line: string) => {
          const values = line.split(separator);
          const mappingRecord: MappingRecord = {
            structureid: '',
            catid: '',
            docid: '',
            nodeid: '',
            cathasdocuments: '',
          };

          for (let i = 0; i < header.length; i++) {
            mappingRecord[header[i] as keyof MappingRecord] = values[i];
          }

          return mappingRecord;
        });
      })
      .finally(() => {
        resolve(mappingRecords);
      });
  });
}

export async function getStaticRedirects(httpClient: HttpClient): Promise<StaticRedirect[]> {
  const queryStaticRedirects = async () => {
    const port = location.port && location.port !== '80' && location.port !== '443' ? `:${location.port}` : '';
    const protocol = location.protocol === 'https:' ? 'https' : 'http';
    const response = await firstValueFrom(
      httpClient.get(`${protocol}://${location.hostname || 'localhost'}${port}/static_redirects.csv`, {
        responseType: 'text',
      })
    );

    return response;
  };

  return new Promise((resolve) => {
    let staticRedirects: StaticRedirect[] = [];

    // Parse CSV response and populate static redirects
    queryStaticRedirects()
      .then((response) => {
        const headerLine = response.split('\n')[0]; // Get header row
        const separator = headerLine.includes(';') ? ';' : ',';
        const header = headerLine.split(separator);
        const lines = response.split('\n').slice(1); // Skip header row

        staticRedirects = lines.map((line: string) => {
          const values = line.split(separator);
          const staticRedirect: StaticRedirect = {
            source: '',
            target: '',
          };

          for (let i = 0; i < header.length; i++) {
            staticRedirect[header[i] as keyof StaticRedirect] = values[i];
          }

          return staticRedirect;
        });
      })
      .finally(() => {
        resolve(staticRedirects);
      });
  });
}
